import React, { useMemo } from 'react';
import Chart from 'react-apexcharts';
import { Card, CardHeader, CardBody } from 'reactstrap';
import { CompareDataBy, CompareChartType, CompareGraphPoint } from 'src/slices/Compare/reducer';
import { COMPARE_METRICS, formatMetricValue, getChangeColor, capPercentChange } from './compareUtils';

interface CompareChartProps {
  metricKey: string;
  range1Data: CompareGraphPoint[];
  range2Data: CompareGraphPoint[];
  dataBy: CompareDataBy;
  chartType: CompareChartType;
  variant: 'featured' | 'mini';
}

const SERIES_COLORS = {
  current:  '#038edc',
  previous: '#adb5bd',
} as const;

const RESET_ZOOM_ICON = `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#038edc" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/></svg>`;

function getResetEl(chartEl: HTMLElement): HTMLElement | null {
  return chartEl.querySelector('.apexcharts-reset-icon');
}


const CompareChart: React.FC<CompareChartProps> = ({
  metricKey,
  range1Data,
  range2Data,
  dataBy,
  chartType,
  variant,
}) => {
  const metricDef = COMPARE_METRICS.find(m => m.key === metricKey);
  const height = variant === 'featured' ? 350 : 280;
  const showToolbar = true;
  const showYTitle = variant === 'featured';

  // Extract numeric values and P1/P2/P3 x-axis labels
  const { categories, series } = useMemo(() => {
    const r1Values = range1Data.map(p =>
      typeof p[metricKey] === 'number' ? (p[metricKey] as number) : 0
    );
    const r2Values = range2Data.map(p =>
      typeof p[metricKey] === 'number' ? (p[metricKey] as number) : 0
    );
    const maxLen = Math.max(r1Values.length, r2Values.length);
    const cats = Array.from({ length: maxLen }, (_, i) => `P${i + 1}`);

    return {
      categories: cats,
      series: [
        { name: 'Current', data: r1Values },
        { name: 'Previous', data: r2Values },
      ],
    };
  }, [range1Data, range2Data, metricKey]);

  const options = useMemo((): any => {
    // Custom tooltip — must be inside useMemo to capture fresh closure values.
    const customTooltip = ({ series: s, dataPointIndex }: any) => {
      if (!metricDef) return '';

      const currentVal: number | null = s[0]?.[dataPointIndex] ?? null;
      const prevVal: number | null = s[1]?.[dataPointIndex] ?? null;

      const r1Period = range1Data[dataPointIndex]?.period ?? null;
      const r2Period = range2Data[dataPointIndex]?.period ?? null;

      const currentStr = currentVal != null ? formatMetricValue(currentVal, metricDef.format) : '—';
      const prevStr    = prevVal    != null ? formatMetricValue(prevVal,    metricDef.format) : '—';

      let pctStr   = 'N/A';
      let pctColor = '#6c757d';
      let arrow    = '';

      if (currentVal != null && prevVal != null && prevVal !== 0) {
        const pct = ((currentVal - prevVal) / prevVal) * 100;
        pctStr   = capPercentChange(pct);
        pctColor = getChangeColor(pct, metricDef.higherIsBetter);
        arrow    = pct > 0 ? '&#9650;' : pct < 0 ? '&#9660;' : '';
      }

      return `<div style="padding:12px 16px;min-width:220px;font-family:inherit;">
        <table style="width:100%;border-collapse:collapse;">
          <tr>
            <td colspan="2" style="padding-bottom:8px;border-bottom:1px solid #e9ecef;">
              <span style="font-weight:500;">${metricDef.label}</span>
            </td>
          </tr>
          <tr>
            <td style="padding:6px 0 2px;vertical-align:top;">
              <span style="display:inline-block;width:10px;height:10px;border-radius:2px;background:${SERIES_COLORS.current};margin-right:6px;vertical-align:middle;"></span>
              <span style="color:#555;">Current</span>
              ${r1Period ? `<div style="color:#999;font-size:0.75rem;margin-left:16px;">${r1Period}</div>` : ''}
            </td>
            <td style="padding:6px 0 2px;text-align:right;font-weight:600;vertical-align:top;">
              ${currentStr}
            </td>
          </tr>
          <tr>
            <td style="padding:2px 0 6px;vertical-align:top;">
              <span style="display:inline-block;width:10px;height:10px;border-radius:2px;background:${SERIES_COLORS.previous};margin-right:6px;vertical-align:middle;"></span>
              <span style="color:#555;">Previous</span>
              ${r2Period ? `<div style="color:#999;font-size:0.75rem;margin-left:16px;">${r2Period}</div>` : ''}
            </td>
            <td style="padding:2px 0 6px;text-align:right;font-weight:500;vertical-align:top;color:#6c757d;">
              ${prevStr}
            </td>
          </tr>
          <tr>
            <td colspan="2" style="padding-top:6px;border-top:1px solid #e9ecef;text-align:right;">
              <span style="font-weight:600;color:${pctColor};">${arrow} ${pctStr}</span>
            </td>
          </tr>
        </table>
      </div>`;
    };

    return {
      chart: {
        type: chartType,
        height,
        toolbar: {
          show: showToolbar,
          tools: { download: true, zoom: true, pan: true, reset: RESET_ZOOM_ICON },
        },
        animations: { enabled: true, easing: 'easeinout', speed: 800 },
        redrawOnParentResize: true,
        redrawOnWindowResize: true,
        events: {
          zoomed: (chart: any, { xaxis }: any) => {
            const el = getResetEl(chart.el);
            if (!el) return;
            const globals = chart.w.globals;
            const initMin = globals.initialMinX;
            const initMax = globals.initialMaxX;
            // Only show reset icon when the view is narrower than the full data range.
            // Scroll-zooming out beyond the original range also fires `zoomed`, so we
            // check that the current window is strictly inside the original bounds.
            const isZoomedIn = (initMin != null && initMax != null && xaxis)
              ? (xaxis.min > initMin + 0.5 || xaxis.max < initMax - 0.5)
              : true;
            el.style.display = isZoomedIn ? 'flex' : '';
          },
          beforeResetZoom: (chart: any) => {
            const el = getResetEl(chart.el);
            if (el) el.style.display = '';
          },
        },
      },
      colors: [SERIES_COLORS.current, SERIES_COLORS.previous],
      stroke: chartType === 'line' ? {
        curve: 'smooth',
        width: [3, 2],
        dashArray: [0, 5],
      } : { width: 1 },
      plotOptions: chartType === 'bar' ? {
        bar: {
          horizontal: false,
          columnWidth: '55%',
          borderRadius: 4,
          dataLabels: { position: 'top' },
        },
      } : {},
      fill: chartType === 'bar' ? { opacity: [0.85, 0.5] } : {},
      dataLabels: { enabled: false },
      xaxis: {
        type: 'category',
        categories,
        labels: {
          rotate: -45,
          rotateAlways: false,
          style: { fontSize: '11px' },
        },
      },
      yaxis: {
        title: showYTitle && metricDef ? { text: metricDef.label } : { text: undefined },
        labels: {
          formatter: (val: number) => {
            if (val == null) return '';
            return metricDef ? formatMetricValue(val, metricDef.format) : String(val);
          },
        },
      },
      tooltip: {
        shared: false,
        intersect: true,
        custom: customTooltip,
        followCursor: true,
        offsetY: -20,
      },
      legend: {
        position: 'bottom',
        horizontalAlign: 'center',
        markers: { width: 12, height: 12, radius: 2 },
        onItemClick: { toggleDataSeries: false },
        onItemHover: { highlightDataSeries: true },
      },
      markers: chartType === 'line' ? {
        size: [5, 4],
        strokeWidth: 0,
        hover: { size: 7 },
      } : {},
      grid: {
        borderColor: '#f1f1f1',
        strokeDashArray: 4,
      },
    };
  }, [categories, chartType, height, showToolbar, showYTitle, range1Data, range2Data, metricKey, metricDef]);

  if (!metricDef) return null;

  const cardClass = variant === 'mini' ? 'compare-mini-chart' : 'compare-featured-chart';

  return (
    <Card className={cardClass}>
      <CardHeader className="py-2 px-3">
        <h6 className="mb-0 fw-medium">{metricDef.label}</h6>
      </CardHeader>
      <CardBody className={variant === 'mini' ? 'pt-0 pb-0' : undefined}>
        <Chart
          key={`compare-chart-${metricKey}-${dataBy}-${chartType}`}
          options={options}
          series={series}
          type={chartType}
          height={height}
        />
      </CardBody>
    </Card>
  );
};

export default CompareChart;
